import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiPlus,
    FiEdit2,
    FiTrash2,
    FiEye,
    FiSearch,
    FiAlertTriangle,
    FiPackage,
    FiRefreshCw,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import inventoryService from "../../services/inventoryService";
import Pagination from "../../components/common/Pagination";
import ConfirmModal from "../../components/common/ConfirmModal";

export default function ItemList() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { academicYear } = useApp();

    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [search, setSearch] = useState("");
    const [catFilter, setCatFilter] = useState("");
    const [lowStockOnly, setLowStockOnly] = useState(
        searchParams.get("lowStock") === "true",
    );
    const [page, setPage] = useState(1);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const res = await inventoryService.getItems({
                academicYear: academicYear?._id,
                search,
                category: catFilter,
                lowStock: lowStockOnly ? "true" : "",
                page,
                limit: 20,
            });
            setItems(res.data);
            setPagination(res.pagination);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [academicYear?._id, search, catFilter, lowStockOnly, page]);

    useEffect(() => {
        inventoryService
            .getCategories(academicYear?._id ? { academicYear: academicYear._id } : {})
            .then((r) => setCategories(r.data))
            .catch(() => { });
    }, [academicYear?._id]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);
    useEffect(() => {
        setPage(1);
    }, [search, catFilter, lowStockOnly]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await inventoryService.deleteItem(deleteTarget._id);
            toast.success(res.message);
            setDeleteTarget(null);
            fetchItems();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    const isLow = (item) =>
        item.minStockLevel > 0 && item.currentStock <= item.minStockLevel;

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Inventory Items</h1>
                    <p className="page-subtitle">
                        {pagination?.total ?? 0} item{pagination?.total !== 1 ? "s" : ""}
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button
                        className="btn btn-outline btn-sm"
                        onClick={fetchItems}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                    >
                        <FiRefreshCw size={12} />
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/inventory/items/new")}
                    >
                        <FiPlus size={14} /> Add Item
                    </button>
                </div>
            </div>

            <div className="card">
                {/* Filters */}
                <div
                    style={{
                        padding: "0.75rem 1rem",
                        borderBottom: "1px solid var(--border)",
                        display: "flex",
                        gap: "0.65rem",
                        flexWrap: "wrap",
                        alignItems: "center",
                    }}
                >
                    <div className="search-bar">
                        <FiSearch size={14} color="#9ca3af" />
                        <input
                            placeholder="Search name, code, location…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        className="form-control"
                        style={{ width: 190, fontSize: 13 }}
                        value={catFilter}
                        onChange={(e) => setCatFilter(e.target.value)}
                    >
                        <option value="">All Categories</option>
                        {categories.map((c) => (
                            <option key={c._id} value={c._id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                    <label
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.4rem",
                            fontSize: 13,
                            cursor: "pointer",
                            color: lowStockOnly ? "#dc2626" : "var(--text-muted)",
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={lowStockOnly}
                            onChange={(e) => setLowStockOnly(e.target.checked)}
                        />
                        <FiAlertTriangle size={13} /> Low stock only
                    </label>
                    {(search || catFilter || lowStockOnly) && (
                        <button
                            className="btn btn-outline btn-sm"
                            onClick={() => {
                                setSearch("");
                                setCatFilter("");
                                setLowStockOnly(false);
                            }}
                        >
                            Clear
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="loading-center">
                        <div className="spinner" />
                        Loading items…
                    </div>
                ) : items.length === 0 ? (
                    <div className="empty-state">
                        <FiPackage
                            size={48}
                            style={{
                                opacity: 0.25,
                                display: "block",
                                margin: "0 auto 0.75rem",
                            }}
                        />
                        <p>
                            {lowStockOnly ? "No low-stock items found." : "No items found."}
                        </p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Item</th>
                                    <th>Category</th>
                                    <th>Code</th>
                                    <th>Location</th>
                                    <th style={{ textAlign: "center" }}>Current Stock</th>
                                    <th style={{ textAlign: "center" }}>Min Level</th>
                                    <th style={{ textAlign: "right" }}>Unit Price</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, i) => {
                                    const low = isLow(item);
                                    const color = item.category?.color || "#6b7280";
                                    return (
                                        <tr
                                            key={item._id}
                                            style={{ background: low ? "#fff5f5" : "transparent" }}
                                        >
                                            <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                {(page - 1) * 20 + i + 1}
                                            </td>
                                            <td>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "0.5rem",
                                                    }}
                                                >
                                                    {low && (
                                                        <FiAlertTriangle
                                                            size={13}
                                                            color="#dc2626"
                                                            title="Low stock!"
                                                        />
                                                    )}
                                                    <div>
                                                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                                                            {item.name}
                                                        </div>
                                                        {item.description && (
                                                            <div
                                                                style={{
                                                                    fontSize: 11.5,
                                                                    color: "var(--text-muted)",
                                                                    maxWidth: 220,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                            >
                                                                {item.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span
                                                    style={{
                                                        background: `${color}18`,
                                                        color,
                                                        padding: "2px 8px",
                                                        borderRadius: 99,
                                                        fontSize: 11.5,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {item.category?.name || "—"}
                                                </span>
                                            </td>
                                            <td style={{ fontSize: 12, fontFamily: "monospace" }}>
                                                {item.itemCode || "—"}
                                            </td>
                                            <td
                                                style={{ fontSize: 12.5, color: "var(--text-muted)" }}
                                            >
                                                {item.location || "—"}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                <span
                                                    style={{
                                                        fontWeight: 800,
                                                        fontSize: 14,
                                                        color:
                                                            item.currentStock === 0
                                                                ? "#dc2626"
                                                                : low
                                                                    ? "#d97706"
                                                                    : "#16a34a",
                                                    }}
                                                >
                                                    {item.currentStock}
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        color: "var(--text-muted)",
                                                        marginLeft: 3,
                                                    }}
                                                >
                                                    {item.unit}
                                                </span>
                                            </td>
                                            <td
                                                style={{
                                                    textAlign: "center",
                                                    fontSize: 13,
                                                    color: "var(--text-muted)",
                                                }}
                                            >
                                                {item.minStockLevel > 0
                                                    ? `${item.minStockLevel} ${item.unit}`
                                                    : "—"}
                                            </td>
                                            <td style={{ textAlign: "right", fontSize: 13 }}>
                                                {item.unitPrice > 0
                                                    ? `₹ ${item.unitPrice.toLocaleString("en-IN")}`
                                                    : "—"}
                                            </td>
                                            <td>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        gap: "0.35rem",
                                                        justifyContent: "flex-end",
                                                    }}
                                                >
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        onClick={() =>
                                                            navigate(`/inventory/items/${item._id}`)
                                                        }
                                                    >
                                                        <FiEye size={12} />
                                                    </button>
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        onClick={() =>
                                                            navigate(`/inventory/items/${item._id}/edit`)
                                                        }
                                                    >
                                                        <FiEdit2 size={12} />
                                                    </button>
                                                    <button
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => setDeleteTarget(item)}
                                                    >
                                                        <FiTrash2 size={12} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                <Pagination pagination={pagination} onPageChange={setPage} />
            </div>

            {deleteTarget && (
                <ConfirmModal
                    title="Delete Item"
                    message={`Delete "${deleteTarget.name}"? All transaction history must be deleted first.`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}
